/**
 * MessagePack `/slots-mp` result decoding.
 * Positional arrays are primary; named fields are kept as fallbacks for `/slots` JSON.
 * @see MessagePackMigrationDoc
 */

export const ActionStatus = {
  Success: 200,
  Rejected: 400,
  Unauthorized: 401,
  InsufficientBalance: 402,
  GameUnavailable: 404,
  ServerFailure: 500,
};

/** @param {unknown} v */
export function num(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/** @param {unknown} raw @param {number} i @param {string} name */
export function at(raw, i, name) {
  if (Array.isArray(raw)) return raw[i];
  if (raw && typeof raw === 'object') return raw[name];
  return undefined;
}

/**
 * Unpack `int[,]` as `[reels, rows, flat]` or pass through nested arrays.
 * @param {unknown} value
 * @returns {number[][]}
 */
export function toMatrix(value) {
  if (!Array.isArray(value)) return [];
  const packed =
    value.length === 3 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number' &&
    Array.isArray(value[2]);
  if (!packed) {
    return value.map((col) => (Array.isArray(col) ? col.map(Number) : []));
  }
  const [reels, rows, flat] = value;
  return Array.from({ length: reels }, (_, i) =>
    flat.slice(i * rows, (i + 1) * rows).map(Number),
  );
}

/** @param {unknown} raw */
export function decodeWinLine(raw) {
  if (Array.isArray(raw)) {
    return {
      symbol: num(raw[0]),
      line: num(raw[1]),
      count: num(raw[2]),
      lineOdd: num(raw[3]),
      multiplier: num(raw[4]),
      lineWinAmount: num(raw[5]),
      lineWinCoinAmount: num(raw[6]),
    };
  }
  return {
    symbol: num(raw?.symbol ?? raw?.Symbol),
    line: num(raw?.line ?? raw?.Line),
    count: num(raw?.count ?? raw?.Count),
    lineOdd: num(raw?.lineOdd ?? raw?.LineOdd),
    multiplier: num(raw?.multiplier ?? raw?.Multiplier),
    lineWinAmount: num(raw?.lineWinAmount ?? raw?.LineWinAmount),
    lineWinCoinAmount: num(raw?.lineWinCoinAmount ?? raw?.LineWinCoinAmount),
  };
}

/** @param {unknown} raw */
export function decodeViewResult(raw) {
  if (raw == null) {
    return {
      matrix: [],
      additional: { wins: [], expandingWild: [], expandingWildMultipliers: [] },
    };
  }
  const a = raw?.a ?? raw?.additional ?? raw?.Additional ?? {};
  const matrixRaw = raw?.m ?? raw?.matrix ?? raw?.Matrix;
  return {
    matrix: toMatrix(matrixRaw),
    additional: {
      wins: (a.w ?? a.wins ?? a.Wins ?? []).map(decodeWinLine),
      expandingWild: (a.exw ?? a.expandingWild ?? a.ExpandingWild ?? []).map(num),
      expandingWildMultipliers: (
        a.exwm ??
        a.expandingWildMultipliers ??
        a.ExpandingWildMultipliers ??
        []
      ).map(num),
    },
  };
}

/**
 * Normalize currency from `[code, rate, precision]` or named fields.
 * @param {unknown} raw
 */
function decodeCurrency(raw) {
  if (Array.isArray(raw)) {
    return {
      currencyCode: raw[0] != null ? String(raw[0]).trim().toUpperCase() : null,
      rate: num(raw[1]),
      precision: num(raw[2]),
    };
  }
  if (raw && typeof raw === 'object') {
    const code = raw.currencyCode ?? raw.CurrencyCode ?? raw.code ?? raw.Code;
    return {
      currencyCode: code != null ? String(code).trim().toUpperCase() : null,
      rate: num(raw.rate ?? raw.Rate),
      precision: num(raw.precision ?? raw.Precision),
    };
  }
  return { currencyCode: null, rate: 0, precision: 2 };
}

/**
 * @param {unknown} body spin result body (positional or named)
 */
export function decodeSpinBody(body) {
  if (body == null) return null;
  const view = decodeViewResult(at(body, 3, 'viewResult') ?? body?.ViewResult);
  const balance = num(at(body, 0, 'balance') ?? body?.Balance);
  const win = num(at(body, 1, 'win') ?? body?.winAmount ?? body?.WinAmount);
  const odd = num(at(body, 2, 'odd') ?? body?.Odd);
  return {
    balance,
    win,
    winAmount: win,
    odd,
    viewResult: view,
    matrix: view.matrix,
    additional: view.additional,
  };
}

/**
 * GameActionResult → shape GameApp already reads.
 * @param {unknown} raw
 */
export function decodeGameActionResult(raw) {
  const payload = raw?.arguments?.[0] ?? raw;
  const actionStatus = num(at(payload, 3, 'actionStatus') ?? payload?.ActionStatus);
  const actionType = at(payload, 1, 'actionType') ?? payload?.ActionType ?? null;
  const bodyRaw = at(payload, 0, 'result') ?? payload?.Result;
  const result = decodeSpinBody(bodyRaw);

  // Legacy JSON envelope (Balance/WinAmount/Result.Matrix on one object)
  if (
    result == null &&
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    (payload.matrix != null ||
      payload.Matrix != null ||
      payload.result != null ||
      payload.Result != null)
  ) {
    const nested = payload.result ?? payload.Result ?? payload;
    const view = decodeViewResult(
      nested?.viewResult ?? nested?.ViewResult ?? nested,
    );
    const win = num(payload.winAmount ?? payload.WinAmount ?? nested?.win ?? nested?.winAmount);
    return {
      actionStatus: actionStatus || 200,
      actionType: actionType ?? 'Spin',
      result: {
        balance: num(payload.balance ?? payload.Balance ?? nested?.balance),
        win,
        winAmount: win,
        odd: num(payload.odd ?? payload.Odd ?? nested?.odd),
        matrix: view.matrix.length ? view.matrix : toMatrix(nested?.matrix ?? nested?.Matrix),
        additional: view.additional,
        viewResult: view,
      },
    };
  }

  return {
    actionStatus,
    actionType,
    result,
  };
}

/** Paytable symbol kinds from StartGameActionResult. */
export const PaytableKind = {
  Line: 0,
  Wild: 1,
  Scatter: 2,
};

/**
 * One paytable entry: positional `[symbol, kind, minCount, odds[]]` or named.
 * Odds are multiples of the spin `Amount` (player bet).
 * @param {unknown} raw
 * @returns {{ symbol: number, kind: number, minCount: number, odds: number[] } | null}
 */
export function decodePaytableEntry(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    const oddsRaw = raw[3];
    return {
      symbol: num(raw[0]),
      kind: num(raw[1]),
      minCount: num(raw[2]),
      odds: Array.isArray(oddsRaw) ? oddsRaw.map(num) : [],
    };
  }
  if (typeof raw !== 'object') return null;
  const oddsRaw = raw.odds ?? raw.Odds;
  return {
    symbol: num(raw.symbol ?? raw.Symbol),
    kind: num(raw.kind ?? raw.Kind),
    minCount: num(raw.minCount ?? raw.MinCount),
    odds: Array.isArray(oddsRaw) ? oddsRaw.map(num) : [],
  };
}

/**
 * @param {unknown} raw
 * @returns {{ symbol: number, kind: number, minCount: number, odds: number[] }[]}
 */
export function decodePaytable(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(decodePaytableEntry).filter(Boolean);
}

/**
 * Map unfinished spin/bonus payloads into InitialState-compatible fields when possible.
 * @param {unknown} unfinished
 */
function decodeUnfinished(unfinished) {
  if (unfinished == null) return null;
  if (Array.isArray(unfinished)) {
    return decodeSpinBody(unfinished);
  }
  if (typeof unfinished !== 'object') return null;
  const view = decodeViewResult(
    unfinished.viewResult ?? unfinished.ViewResult ?? unfinished,
  );
  const win = num(
    unfinished.win ??
      unfinished.winAmount ??
      unfinished.WinAmount ??
      unfinished.pendingWin ??
      unfinished.PendingWin,
  );
  const hasView =
    view.matrix.length > 0 ||
    view.additional.wins.length > 0 ||
    view.additional.expandingWild.length > 0;
  if (!hasView && win <= 0 && unfinished.balance == null && unfinished.Balance == null) {
    return unfinished;
  }
  return {
    ...unfinished,
    balance: num(unfinished.balance ?? unfinished.Balance),
    win,
    winAmount: win,
    matrix: view.matrix.length ? view.matrix : toMatrix(unfinished.matrix ?? unfinished.Matrix),
    additional: view.additional,
    wins: view.additional.wins,
  };
}

/**
 * StartGameActionResult → InitialState-compatible session object.
 * @param {unknown} raw
 */
export function decodeStartGameActionResult(raw) {
  const payload = raw?.arguments?.[0] ?? raw;

  // Already-named legacy InitialState
  if (
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    (payload.quickBets != null ||
      payload.QuickBets != null ||
      payload.defaultBetAmount != null ||
      payload.DefaultBetAmount != null)
  ) {
    const matrix = toMatrix(payload.matrix ?? payload.Matrix ?? payload.initialBoard);
    return {
      ...payload,
      actionStatus: num(payload.actionStatus ?? payload.ActionStatus) || 200,
      balance: num(payload.balance ?? payload.Balance),
      currencyCode:
        payload.currencyCode ??
        payload.CurrencyCode ??
        (typeof payload.currency === 'string' ? payload.currency : null),
      precision: num(payload.precision ?? payload.Precision ?? 2),
      quickBets: (payload.quickBets ?? payload.QuickBets ?? []).map(num),
      defaultBetAmount: num(payload.defaultBetAmount ?? payload.DefaultBetAmount),
      maxOdd: num(payload.maxOdd ?? payload.MaxOdd),
      matrix,
      buyBonusAvailable: Boolean(payload.buyBonusAvailable ?? payload.HasBuyBonus ?? payload.hasBuyBonus),
      paytable: decodePaytable(payload.paytable ?? payload.Paytable),
    };
  }

  const actionStatus = num(at(payload, 0, 'actionStatus') ?? payload?.ActionStatus);
  const balance = num(at(payload, 1, 'balance') ?? payload?.Balance);
  const currency = decodeCurrency(at(payload, 2, 'currency') ?? payload?.Currency);
  const betOptions = (at(payload, 3, 'betOptions') ?? payload?.betOptions ?? payload?.BetOptions ?? [])
    .map(num)
    .filter((n) => Number.isFinite(n));
  const defaultBet = num(at(payload, 4, 'defaultBet') ?? payload?.defaultBet ?? payload?.DefaultBet);
  const maxOdd = num(at(payload, 5, 'maxOdd') ?? payload?.maxOdd ?? payload?.MaxOdd);
  const initialBoard = at(payload, 6, 'initialBoard') ?? payload?.initialBoard ?? payload?.InitialBoard;
  const matrix = toMatrix(initialBoard);
  const buyBonusAvailable = Boolean(
    at(payload, 7, 'buyBonusAvailable') ?? payload?.buyBonusAvailable ?? payload?.BuyBonusAvailable,
  );
  const unfinishedSpin = decodeUnfinished(
    at(payload, 8, 'unfinishedSpin') ?? payload?.unfinishedSpin ?? payload?.UnfinishedSpin,
  );
  const unfinishedBonus = decodeUnfinished(
    at(payload, 9, 'unfinishedBonus') ?? payload?.unfinishedBonus ?? payload?.UnfinishedBonus,
  );
  const paytable = decodePaytable(
    at(payload, 10, 'paytable') ?? payload?.paytable ?? payload?.Paytable,
  );

  /** @type {Record<string, unknown>} */
  const normalized = {
    actionStatus,
    balance,
    currencyCode: currency.currencyCode,
    rate: currency.rate,
    precision: currency.precision,
    Precision: currency.precision,
    quickBets: betOptions,
    QuickBets: betOptions,
    betOptions,
    defaultBetAmount: defaultBet,
    DefaultBetAmount: defaultBet,
    defaultBet,
    maxOdd,
    MaxOdd: maxOdd,
    matrix,
    Matrix: matrix,
    initialBoard: matrix,
    buyBonusAvailable,
    HasBuyBonus: buyBonusAvailable,
    unfinishedSpin,
    unfinishedBonus,
    paytable,
  };

  // Surface unfinished board / wins for interrupted-round resume helpers.
  const unfinished = unfinishedSpin ?? unfinishedBonus;
  if (unfinished && typeof unfinished === 'object') {
    if (Array.isArray(unfinished.matrix) && unfinished.matrix.length) {
      normalized.matrix = unfinished.matrix;
      normalized.Matrix = unfinished.matrix;
    }
    if (unfinished.additional) {
      normalized.additional = unfinished.additional;
      normalized.Additional = unfinished.additional;
      normalized.wins = unfinished.additional.wins;
      normalized.Wins = unfinished.additional.wins;
      normalized.expandingWild = unfinished.additional.expandingWild;
    }
    const pendingWin = num(unfinished.winAmount ?? unfinished.win);
    if (pendingWin > 0) {
      normalized.pendingWin = pendingWin;
      normalized.PendingWin = pendingWin;
      normalized.winAmount = pendingWin;
      normalized.WinAmount = pendingWin;
      normalized.needsCollect = true;
      normalized.NeedsCollect = true;
    }
  }

  return normalized;
}

/**
 * GetBalanceResult → `{ balance }`
 * @param {unknown} raw
 */
export function decodeGetBalanceResult(raw) {
  const payload = raw?.arguments?.[0] ?? raw;
  return {
    balance: num(at(payload, 0, 'balance') ?? payload?.Balance),
    responseCode: num(at(payload, 1, 'responseCode') ?? payload?.ResponseCode),
  };
}

/**
 * @param {unknown} payload ForceDisconnect callback payload
 * @returns {string | null}
 */
export function readForceDisconnectConnectionId(payload) {
  if (payload == null) return null;
  if (typeof payload === 'string') return payload;
  const id =
    payload.connectionId ??
    payload.ConnectionId ??
    payload.connectionID ??
    payload.ConnectionID;
  return id != null ? String(id) : null;
}
